import type { LevelDefinition } from "@algoquest/schemas";
import { createCampaignLevel } from "./levelFactory";

/**
 * Phase 15 -- World 8: "Insertion Sort" (Master Spec Section 5), campaign
 * levels 71-80. Teaches: sorted region, incoming/current element, comparison, shift, insertion, expanding sorted region.
 * Algorithm(s) used in this world: insertion-sort.
 */
export const world08InsertionSortLevels: LevelDefinition[] = [
  createCampaignLevel({
    id: "campaign-071",
    world: 8,
    levelInWorld: 1,
    globalLevelNumber: 71,
    algorithm: "insertion-sort",
    values: [5, 3, 8, 1, 6],
    sortDirection: "ascending",
    learningObjectives: [
      "Learn Insertion Sort: grow a sorted region at the front by inserting one item at a time.",
      "See the very first item treated as an already-sorted region of size one.",
    ],
    notes: "A first practice run: take each remaining item and insert it into its correct place in the sorted region.",
  }),
  createCampaignLevel({
    id: "campaign-072",
    world: 8,
    levelInWorld: 2,
    globalLevelNumber: 72,
    algorithm: "insertion-sort",
    values: [5, 3, 8, 1, 6],
    sortDirection: "descending",
    learningObjectives: [
      "Apply the same insert-into-place idea while growing a descending sorted region.",
      "Compare where each value gets inserted here versus in level 71's ascending version.",
    ],
    notes: "Same five values as level 71, this time growing a largest-to-smallest sorted region.",
  }),
  createCampaignLevel({
    id: "campaign-073",
    world: 8,
    levelInWorld: 3,
    globalLevelNumber: 73,
    algorithm: "insertion-sort",
    values: [2, 3, 5, 4, 6, 7],
    sortDirection: "ascending",
    learningObjectives: [
      "See how few shifts a nearly-sorted dataset needs -- Insertion Sort's best case.",
      "Understand why Insertion Sort is especially efficient when data starts close to sorted.",
    ],
    notes: "Only the value 4 is out of place -- inserting it costs just one shift.",
  }),
  createCampaignLevel({
    id: "campaign-074",
    world: 8,
    levelInWorld: 4,
    globalLevelNumber: 74,
    algorithm: "insertion-sort",
    values: [9, 4, 12, 2, 7, 1],
    sortDirection: "ascending",
    learningObjectives: [
      "Practice comparing the incoming item against the sorted region from right to left.",
      "See the shift operation move larger items one position to make room.",
    ],
    notes: "A 6-item unsorted list -- an ordinary ascending insertion sort.",
  }),
  createCampaignLevel({
    id: "campaign-075",
    world: 8,
    levelInWorld: 5,
    globalLevelNumber: 75,
    algorithm: "insertion-sort",
    values: [9, 4, 12, 2, 7, 1],
    sortDirection: "descending",
    learningObjectives: [
      "Sort the same six values as level 74 by growing a descending sorted region instead.",
      "Reinforce that comparison direction flips, but the shift-and-insert mechanic does not.",
    ],
    notes: "Same values as level 74, sorted largest to smallest.",
  }),
  createCampaignLevel({
    id: "campaign-076",
    world: 8,
    levelInWorld: 6,
    globalLevelNumber: 76,
    algorithm: "insertion-sort",
    values: [21, 18, 15, 12, 9, 6, 3],
    sortDirection: "ascending",
    learningObjectives: [
      "See the worst case for Insertion Sort: every incoming item must shift all the way to the front.",
      "Contrast this maximum-shift case with level 73's minimum-shift case.",
    ],
    notes: "Exactly reverse-ordered -- each new item requires shifting past every item already placed.",
  }),
  createCampaignLevel({
    id: "campaign-077",
    world: 8,
    levelInWorld: 7,
    globalLevelNumber: 77,
    algorithm: "insertion-sort",
    values: [18, 4, 27, 9, 15, 2, 22],
    sortDirection: "ascending",
    learningObjectives: [
      "Apply the sorted-region-grows mechanic to a larger, 7-item dataset.",
      "Track exactly where the boundary between sorted and unsorted sits after each insertion.",
    ],
    notes: "A 7-item unsorted list, growing an ascending sorted region.",
  }),
  createCampaignLevel({
    id: "campaign-078",
    world: 8,
    levelInWorld: 8,
    globalLevelNumber: 78,
    algorithm: "insertion-sort",
    values: [18, 4, 27, 9, 15, 2, 22],
    sortDirection: "descending",
    learningObjectives: [
      "Sort the same seven values as level 77 by growing a descending sorted region.",
      "Build fluency switching the insertion comparison direction between rounds.",
    ],
    notes: "Same values as level 77, descending goal instead of ascending.",
  }),
  createCampaignLevel({
    id: "campaign-079",
    world: 8,
    levelInWorld: 9,
    globalLevelNumber: 79,
    algorithm: "insertion-sort",
    values: [21, 8, 34, 3, 19, 27, 11, 6],
    sortDirection: "ascending",
    learningObjectives: [
      "Complete a full ascending sort on the largest dataset in this world.",
      "Consolidate sorted-region growth, comparison, shifting, and insertion together.",
    ],
    notes: "World 8's largest dataset -- a genuine multi-insertion ascending sort.",
  }),
  createCampaignLevel({
    id: "campaign-080",
    world: 8,
    levelInWorld: 10,
    globalLevelNumber: 80,
    algorithm: "insertion-sort",
    values: [21, 8, 34, 3, 19, 27, 11, 6],
    sortDirection: "descending",
    learningObjectives: [
      "Complete the same dataset as level 79 sorted from largest to smallest.",
      "Reflect on how Insertion Sort compares to Bubble Sort and Selection Sort before the Sorting Challenge.",
    ],
    notes: "World 8 capstone -- same eight values as level 79, sorted in the opposite direction.",
  }),
];
